// ── Polymarket ticker parser ────────────────────────────────────
//
// Polymarket tickers reach g1 in one of two shapes:
//   1. Hydra-synthesized: `PM-{sport}-{away}-{home}-{YYYY-MM-DD}{suffix}`
//      Suffixes: `''` (moneyline), `-spread-{home|away}-{N}pt{N}`, or
//      `-total-{N}pt{N}`. `pt` is Polymarket's slug-safe stand-in for `.`.
//   2. Raw CLOB condition id: `0x{64-hex}`. We have no structural info
//      from the string alone — the human-readable label has to come from
//      somewhere else (HydraMarket question, API-fetched title map, etc.).
//
// `parsePolymarketTicker` produces the same `ParsedTicker` shape kalshi
// uses so the rest of the label layer (describeMarket, displayTitle,
// TickerDisplay) can branch on `category === 'polymarket'` without
// caring about the underlying ticker format.
//
// `detectVenue` is the prefix-based discriminator used by `parseTicker`
// in parse.ts to dispatch between this and the kalshi parser.

import type { ParsedTicker } from './types';

const PM_REGEX =
  /^PM-([a-z]+)-([a-z0-9]+)-([a-z0-9]+)-(\d{4}-\d{2}-\d{2})(.*)$/i;

const SOCCER_PM_SPORTS = new Set(['fif', 'fifwc']);

export function detectVenue(ticker: string): 'kalshi' | 'polymarket' {
  const t = String(ticker ?? '').trim();
  if (!t) return 'kalshi';
  if (t.startsWith('PM-') || t.startsWith('pm-')) return 'polymarket';
  if (t.toLowerCase().startsWith('0x')) return 'polymarket';
  return 'kalshi';
}

function truncatePolyId(raw: string): string {
  if (raw.length <= 16) return raw;
  return `${raw.slice(0, 8)}…${raw.slice(-4)}`;
}

export function parsePolymarketTicker(ticker: string): ParsedTicker {
  const raw = String(ticker ?? '').trim();

  const match = raw.match(PM_REGEX);
  if (!match) {
    // Raw condition id (0x…) or any other non-PM shape. We can't derive
    // teams/sport/date from the string; consumers should rely on an
    // external title (API-fetched, HydraMarket question, etc.).
    const display = truncatePolyId(raw);
    return {
      raw,
      category: 'polymarket',
      series: 'PM',
      date: null,
      game: null,
      player: null,
      spread: null,
      total: null,
      tennis: null,
      outcome: null,
      shortLabel: display,
      fullLabel: `Polymarket ${display}`,
    };
  }

  const [, sportRaw, awayRaw, homeRaw, date, suffix] = match;
  const sport = SOCCER_PM_SPORTS.has(sportRaw.toLowerCase())
    ? 'SOCCER'
    : sportRaw.toUpperCase();
  const awayCode = awayRaw.toUpperCase();
  const homeCode = homeRaw.toUpperCase();
  const outcomeRaw = suffix.match(/^-(?:moneyline-)?([a-z0-9]+)$/i)?.[1] ?? null;
  const outcome =
    outcomeRaw == null
      ? null
      : outcomeRaw.toLowerCase() === 'draw'
        ? 'DRAW'
        : outcomeRaw.toUpperCase();
  const outcomeLabel =
    outcome === 'DRAW' ? 'Draw' : outcome ? `${outcome} wins` : null;
  const short = `${awayCode} @ ${homeCode}`;
  const full = `${awayCode} @ ${homeCode} (Poly ${sport})${outcomeLabel ? ` · ${outcomeLabel}` : ''}`;

  return {
    raw,
    category: 'polymarket',
    // `series` is overloaded across parsers; for poly we stash the
    // PM-{SPORT} stem here so describeMarket can recover the sport label
    // without re-parsing the raw ticker.
    series: `PM-${sport}`,
    date,
    game: { awayCode, homeCode },
    player: null,
    spread: null,
    total: null,
    tennis: null,
    outcome,
    shortLabel: short,
    fullLabel: full,
  };
}
